import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

// Utility: Enforce a minimum slice value but keep original values for display
function normalizePieValues(values, minValue) {
  if (!Array.isArray(values)) {
    throw new Error("Input 'values' must be an array of numbers.");
  }

  return values.map((v) => {
    if (typeof v !== 'number' || Number.isNaN(v)) {
      throw new Error('All values must be valid numbers.');
    }
    return v < minValue ? minValue : v;
  });
}

const generateColors = (numColors) => {
  const baseColors = ['#A8E308', '#00FFFF', '#FF1493', '#FF4500'];
  const additionalColors = [];
  const saturation = 50;
  const lightness = 90;

  for (let i = 0; i < numColors - baseColors.length; i++) {
    const hue = (i * 360) / (numColors - baseColors.length);
    additionalColors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }

  return [...baseColors, ...additionalColors].slice(0, numColors);
};

const DonutChartFormat = ({ eventData = {} }) => {
  const labels = useMemo(() => Object.keys(eventData), [eventData]);
  const originalData = useMemo(() => Object.values(eventData), [eventData]);

  const series = useMemo(() => {
    if (!labels.length) return [];
    return normalizePieValues(originalData, 1000);
  }, [labels.length, originalData]);

  const total = useMemo(
    () => originalData.reduce((a, b) => a + b, 0),
    [originalData]
  );

  const colors = useMemo(
    () => generateColors(labels.length),
    [labels.length]
  );

  const chartOptions = useMemo(() => ({
    chart: {
      id: `chart-${labels.join('-')}-${originalData.join('-')}`,
      type: 'donut',
    },
    labels,
    legend: { show: false },
    colors,
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: { show: false },
        },
        expandOnClick: false,
      },
    },
    tooltip: {
      enabled: true,
      theme: 'light',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
      },
      y: {
        formatter: (_value, { dataPointIndex }) => {
          const realValue = originalData[dataPointIndex];
          return realValue != null ? String(realValue) : '0';
        },
      },
    },
  }), [labels, colors, originalData]);

  return (
    <div
      id="donut-chart-format"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: series.length === 0 ? 'center' : undefined,
      }}
    >
      {series.length > 0 ? (
        <ReactApexChart
          options={chartOptions}
          series={series}
          type="donut"
          height={window.innerWidth <= 415 ? 180 : 320}
          width={window.innerWidth <= 415 ? 230 : 520}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '18px',
            color: '#666',
            textAlign: 'center',
          }}
        >
          No data is available.
        </div>
      )}

      {series.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {labels.map((label, index) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '5px 15px',
              }}
            >
              <span
                style={{
                  width: '15px',
                  height: '15px',
                  backgroundColor: colors[index],
                  marginRight: '8px',
                  borderRadius: '3px',
                }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: '#333',
                }}
              >
                {label}:{' '}
                {total > 0
                  ? ((originalData[index] / total) * 100).toFixed(2)
                  : '0.00'}
                %
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonutChartFormat;